import { createContext, useContext } from 'react';
import { useLocation } from 'react-router';

import { MealPhotoQueueResponse } from '@/api/generated/MNT';
import { cn, StatusBadge, StatusBadgeTone } from '@/components/ui';
import { useAuth } from '@/context/appContext';
import { useMealQueueService } from '@/services/MealQueueService';

/** Dense badges for tight rows such as the sidebar, so the queue id keeps its room. */
const DenseContext = createContext(false);

const SummaryBadge = ({
  badgeClassName,
  className,
  label,
  title,
  tone = 'neutral',
}: {
  badgeClassName?: string;
  className?: string;
  label: string;
  title: string;
  tone?: StatusBadgeTone;
}) => (
  <span className={cn('inline-flex', className)}>
    <StatusBadge className={cn(useContext(DenseContext) && 'px-1.5', badgeClassName)} title={title} tone={tone}>
      {label}
    </StatusBadge>
  </span>
);

const QueueLevelIcon = ({ queue }: { queue: MealPhotoQueueResponse }) => {
  const reviewLevel = queue.queue_metadata?.review_effective_level;
  if (!reviewLevel) {
    return null;
  }

  const tone: StatusBadgeTone = reviewLevel === 1 ? 'positive' : reviewLevel === 2 ? 'caution' : 'critical';

  return <SummaryBadge label={`L${reviewLevel}`} title={`Level ${reviewLevel}`} tone={tone} />;
};

export const QueuePriorityIcon = (props: { queue: MealPhotoQueueResponse; size?: 'small' | 'medium' | 'large' }) => (
  <PatientPriorityIcon {...(props.queue?.queue_priority ?? props.queue ?? {})} size={props.size} />
);

export const PatientPriorityIcon = (props: {
  is_priority?: boolean;
  is_ultra_priority?: boolean;
  is_priority_patient?: boolean;
  is_ultra_priority_patient?: boolean;
  reason?: string | null;
  size?: 'small' | 'medium' | 'large';
}) => {
  const ultraPriority = props.is_ultra_priority || props.is_ultra_priority_patient;
  const priority = props.is_priority || props.is_priority_patient;
  const title = props.reason ?? (ultraPriority ? 'ultra priority' : priority ? 'priority' : null);

  if (!title) {
    return null;
  }

  return (
    <SummaryBadge
      badgeClassName={ultraPriority ? 'bg-critical text-ink-inverse' : undefined}
      label="P"
      title={title}
      tone="critical"
    />
  );
};

const QueueOwnerIcon = ({ queue }: { queue: MealPhotoQueueResponse }) => {
  const mpq = useMealQueueService();
  const location = useLocation();
  const { authInfo } = useAuth();

  if (!queue.first_reviewer_user_id) {
    return null;
  }

  const ownedQueues = mpq.ownedQueues;
  const [currentPath, currentQueueId] = location.pathname.split('/').slice(-2);
  const currentOwnedQueue =
    currentPath === 'queue-item' && ownedQueues.some(item => item.id === Number(currentQueueId));
  const isCurrentReviewer = queue.first_reviewer_user_id === authInfo?.reviewer_id;
  const label = isCurrentReviewer ? 'you' : queue.first_reviewer_user_id.toString();
  const tone: StatusBadgeTone =
    isCurrentReviewer && currentOwnedQueue ? 'brand' : isCurrentReviewer ? 'critical' : 'neutral';

  return <SummaryBadge label={label} title={`owner: ${label}`} tone={tone} />;
};

const QueueIsAutoLoggedIcon = ({ queue }: { queue: MealPhotoQueueResponse }) =>
  queue.is_processed && !queue.first_reviewer_user_id ? (
    <SummaryBadge label="A" title="auto logged" tone="informative" />
  ) : null;

const CustomItemIcon = () => <SummaryBadge label="C" title="Custom item" tone="caution" />;

export const QueueSummaryIcons = (props: {
  queue: MealPhotoQueueResponse;
  hideClassif?: boolean;
  hideOwner?: boolean;
  showCustomIcon?: boolean;
  dense?: boolean;
}) => {
  const { queue } = props;
  if (!queue) {
    return null;
  }

  return (
    <DenseContext.Provider value={!!props.dense}>
      <span className={cn('inline-flex justify-end', props.dense ? 'gap-0.5' : 'gap-1')}>
        {!props.hideClassif && <QueueLevelIcon queue={queue} />}
        <QueueIsAutoLoggedIcon queue={queue} />
        <QueuePriorityIcon queue={queue} />
        {props.showCustomIcon && <CustomItemIcon />}
        {!props.hideOwner && <QueueOwnerIcon queue={queue} />}
      </span>
    </DenseContext.Provider>
  );
};
